import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Save, Trash2, Bot, Sparkles, Code, TestTube, Rocket, Brain } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const PRESET_AGENTS = [
  { id: 'frontend', name: 'Frontend', icon: Code, color: 'text-blue-600 bg-blue-50', desc: 'React, Tailwind, UI components' },
  { id: 'backend', name: 'Backend', icon: Bot, color: 'text-emerald-600 bg-emerald-50', desc: 'FastAPI, MongoDB, APIs' },
  { id: 'testing', name: 'Testing', icon: TestTube, color: 'text-amber-600 bg-amber-50', desc: 'pytest, Playwright, QA' },
  { id: 'deploy', name: 'Deploy', icon: Rocket, color: 'text-purple-600 bg-purple-50', desc: 'Vercel, Railway, Docker' },
];

const CustomAgentsPage = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [newAgent, setNewAgent] = useState({ name: '', description: '', system_prompt: '', icon: 'Bot', base_agent: '' });

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await api.get('/agents/custom');
        setAgents(res.data.agents || []);
      } catch { /* empty */ }
      setLoading(false);
    };
    fetchAgents();
  }, []);

  const handleCreate = async () => {
    if (!newAgent.name.trim() || !newAgent.system_prompt.trim()) {
      toast({ title: 'Error', description: 'Name and system prompt are required', variant: 'destructive' });
      return;
    }
    try {
      const res = await api.post('/agents/custom', newAgent);
      if (res.data.success) {
        setAgents(prev => [...prev, res.data.agent]);
        setNewAgent({ name: '', description: '', system_prompt: '', icon: 'Bot', base_agent: '' });
        toast({ title: 'Agent Created', description: `${newAgent.name} is ready to use in your projects.` });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to create agent', variant: 'destructive' });
    }
  };

  const handleDelete = async (agentId) => {
    try {
      await api.delete(`/agents/custom/${agentId}`);
      setAgents(prev => prev.filter(a => a.id !== agentId));
      toast({ title: 'Agent Deleted' });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleUpdate = async (agent) => {
    try {
      await api.put(`/agents/custom/${agent.id}`, agent);
      setEditingId(null);
      toast({ title: 'Agent Updated' });
    } catch {
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4 text-muted-foreground hover:text-foreground" data-testid="agents-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Button>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="custom-agents-title">Custom Agents</h1>
            <p className="text-sm text-muted-foreground">Create and edit AI agents with custom system prompts</p>
          </div>
        </div>

        {/* Built-in agents */}
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Built-in Agents</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {PRESET_AGENTS.map(a => (
              <Card key={a.id} className="border-border">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${a.color}`}>
                    <a.icon className="w-4.5 h-4.5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">{a.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{a.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Create new agent */}
        <Card className="border-border mb-8">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" /> Create New Agent
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Agent Name</label>
                  <Input value={newAgent.name} onChange={e => setNewAgent({...newAgent, name: e.target.value})} placeholder="e.g., Documentation Writer"
                    className="bg-muted border-border text-foreground" data-testid="new-agent-name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Base Agent (optional)</label>
                  <select value={newAgent.base_agent} onChange={e => setNewAgent({...newAgent, base_agent: e.target.value})}
                    className="w-full h-10 rounded-md border border-border bg-muted text-foreground px-3 text-sm" data-testid="new-agent-base">
                    <option value="">Start from scratch</option>
                    <option value="frontend">Frontend Agent</option>
                    <option value="backend">Backend Agent</option>
                    <option value="testing">Testing Agent</option>
                    <option value="deploy">Deploy Agent</option>
                    <option value="coordinator">E1 Coordinator</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Description</label>
                <Input value={newAgent.description} onChange={e => setNewAgent({...newAgent, description: e.target.value})} placeholder="What this agent specializes in"
                  className="bg-muted border-border text-foreground" data-testid="new-agent-desc" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">System Prompt</label>
                <Textarea value={newAgent.system_prompt} onChange={e => setNewAgent({...newAgent, system_prompt: e.target.value})}
                  placeholder="You are a specialized AI agent that..."
                  className="bg-muted border-border text-foreground min-h-[160px] font-mono text-sm" rows={8} data-testid="new-agent-prompt" />
                <p className="text-xs text-muted-foreground mt-1">This prompt defines how the agent behaves. Be specific about its role, rules, and output format.</p>
              </div>
              <Button onClick={handleCreate} className="bg-primary text-primary-foreground gap-2" data-testid="create-agent-btn">
                <Sparkles className="w-4 h-4" /> Create Agent
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Custom agents list */}
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Your Custom Agents</h2>
          {loading ? (
            <div className="space-y-3">{[1,2].map(i => <div key={i} className="animate-pulse h-20 bg-muted rounded-lg" />)}</div>
          ) : agents.length === 0 ? (
            <Card className="border-border border-dashed">
              <CardContent className="p-8 text-center">
                <Brain className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No custom agents yet. Create one above.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {agents.map(agent => (
                <Card key={agent.id} className="border-border" data-testid={`custom-agent-${agent.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                          <Bot className="w-4.5 h-4.5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{agent.name}</p>
                          <p className="text-xs text-muted-foreground">{agent.description}</p>
                          {agent.base_agent && <Badge className="mt-1 text-[10px]">Based on: {agent.base_agent}</Badge>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(editingId === agent.id ? null : agent.id)} className="h-8 px-2 text-muted-foreground hover:text-foreground">
                          Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(agent.id)} className="h-8 px-2 text-muted-foreground hover:text-red-500" data-testid={`delete-agent-${agent.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    {editingId === agent.id && (
                      <div className="mt-4 pt-4 border-t border-border space-y-3">
                        <Textarea value={agent.system_prompt} onChange={e => setAgents(prev => prev.map(a => a.id === agent.id ? {...a, system_prompt: e.target.value} : a))}
                          className="bg-muted border-border text-foreground min-h-[120px] font-mono text-sm" rows={6} />
                        <Button size="sm" onClick={() => handleUpdate(agent)} className="gap-1.5">
                          <Save className="w-3.5 h-3.5" /> Save Changes
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomAgentsPage;
